<template>
    <div id="org_settings_profile" class="mb-5">
        <div class="card mb-3">
            <div class="card-body">
                <div class="d-md-flex d-block">
                    <div class="flex-shrink-1 profile-image px-2 pe-4">
                        <!-- Photo Preview-->
                        <span :data-name="local_client.id" id="client_id"></span>
                        <div class="profile-image-container mb-3">
                            <img 
                                :src="local_client.image || '/img/images/profile_image_placeholder.png'" 
                                :alt="local_client.name || 'Organization'"
                                :key="local_client.image || 'placeholder'"
                                class="profile-image"
                            >
                            <button 
                                v-if="canEdit('settings')"
                                class="btn btn-secondary btn-icon-only-sm"
                                @click="openImageModal"
                                data-bs-toggle="tooltip"
                                title="Change Profile Image"
                            >
                               <img src="/img/icons/avid-icons/upload.svg" alt="Change Profile Image" class="icon">
                            </button>
                        </div>
                        <div class="text-danger invalid-feedback" v-if="errors.image !== ''">
                            {{errors.image}}
                        </div>
                    </div>
                    <div class="mt-md-0 mt-3 px-2 w-100">
                        <h3>Organizational Profile</h3>
                        <div class="row">
                            <div class="col-12">
                                <!-- Name -->
                                <div class="mb-3">
                                    <label class="form-label">Organization Name</label>
                                    <input 
                                        type="text" 
                                        class="form-control" 
                                        :disabled="!canEdit('settings')"
                                        :class="{'is-invalid': errors.name !== ''}"
                                        name="name" 
                                        v-model="local_client.name" 
                                        @change="validateName()"
                                     aria-label="Organization Name">
                                    <div class="text-danger invalid-feedback" v-show="errors.name !== ''">
                                        {{errors.name}}
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-md-4 col-12">
                                        <div class="mb-3">
                                            <label class="form-label">Vertical</label>
                                            <div class="dropdown">
                                                <select 
                                                    class="form-select" 
                                                    :disabled="!canEdit('settings')"
                                                    id="vertical" 
                                                    v-model="local_client.vertical" 
                                                    :class="{'is-invalid': errors.vertical !== ''}" 
                                                    @change="errors.vertical = ''"
                                                 aria-label="Vertical">
                                                    <option :value="item" v-for="item in vertical_types" :key="item">{{item}}</option>
                                                </select>
                                            </div>
                                            <div class="text-danger invalid-feedback" v-show="errors.vertical !== ''">
                                                {{errors.vertical}}
                                            </div>
                                        </div>
                                    </div>

                                    <div class="col-md-4 col-12">
                                        <div class="mb-3">
                                            <label class="form-label">Operational Area</label>
                                            <select 
                                                class="form-select" 
                                                :disabled="!canEdit('settings')"
                                                id="focus" 
                                                v-model="local_client.focus" 
                                                :class="{'is-invalid': errors.focus !== ''}" 
                                                @change="errors.focus = ''"
                                             aria-label="Operational Area">
                                                <option value="">Select Operational Area</option>
                                                <option value="Regional">Regional</option>
                                                <option value="National">National</option>
                                                <option value="International">International</option>
                                            </select>
                                            <div class="text-danger invalid-feedback" v-show="errors.focus !== ''">
                                                {{errors.focus}}
                                            </div>
                                        </div>
                                    </div>

                                    <div class="col-md-4 col-12 mb-3">
                                        <label class="form-label">Website Domain</label>
                                        <input 
                                            type="text" 
                                            class="form-control" 
                                            :class="{'is-invalid': errors.domain !== ''}"
                                            name="domain" 
                                            :disabled="!canEdit('settings')"
                                            v-model="local_client.domain" 
                                            @change="validateDomain()"
                                            placeholder="example.com"
                                         aria-label="Website Domain">
                                        <div class="text-danger invalid-feedback" v-show="errors.domain !== ''">
                                            {{errors.domain}}
                                        </div>
                                    </div>
                                </div>
                                <div class="mb-3">
                                    <div class="row">
                                        <div class="col-xl-3 col-lg-6 col-12">
                                            <label class="form-label">Dark Color
                                                <img class="info-icon" src="/img/icons/avid-icons/info.svg"  data-bs-toggle="tooltip" data-placement="top" title="Pick a dark color used in titles and other fonts that will be used when generating slides. It should match your branding." alt=""></img>
                                            </label>
                                            <div class="input-group mb-3">
                                                <input 
                                                    type="color"
                                                    :disabled="!canEdit('settings')"
                                                    class="form-control form-control-color"
                                                    v-model="local_client.dark_color"
                                                    :style="{ 'background-color': local_client.dark_color }"
                                                 aria-label="Color picker">
                                                <input 
                                                    type="text"
                                                    class="form-control"
                                                    v-model="local_client.dark_color"
                                                    :disabled="!canEdit('settings')"
                                                 aria-label="Text input">
                                            </div>
                                        </div>
                                        <div class="col-xl-3 col-lg-6 col-12">
                                            <label class="form-label">Accent Color
                                                <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" data-placement="top" title="Pick a bright color that will be used as an accent when generating slides. It should match your branding." alt=""></img>
                                            </label>
                                            <div class="input-group mb-3">
                                                <input 
                                                    type="color"
                                                    :disabled="!canEdit('settings')"
                                                    class="form-control form-control-color"
                                                    v-model="local_client.light_color"
                                                    :style="{ 'background-color': local_client.light_color }"
                                                 aria-label="Color picker">
                                                <input 
                                                    type="text"
                                                    class="form-control"
                                                    v-model="local_client.light_color"
                                                    :disabled="!canEdit('settings')"
                                                 aria-label="Text input">
                                            </div>
                                        </div>
                                        <div class="col-md-6 col-12">
                                            <label class="form-label">Slack Webhook URL
                                                <a href="https://docs.slack.dev" target="_blank">
                                                    <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" data-placement="top" title="Slack Webhook URLs have to be set up for each client so the messages go to a client-specific channel. Click on this icon to add a new webhook or find an existing one." alt=""></img>
                                                </a>
                                            </label>
                                            <input 
                                                type="text" 
                                                class="form-control" 
                                                :class="{'is-invalid': errors.slack_url !== ''}"
                                                name="slack_url" 
                                                v-model="local_client.slack_url" 
                                                @change="validateURL()"
                                                :disabled="!canEdit('settings')"
                                             aria-label="Text input">
                                            <div class="text-danger invalid-feedback" v-show="errors.slack_url !== ''">
                                                {{errors.slack_url}}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="mb-3">
                                    <h4 class="mb-1">Chart Colors for Exports 
                                        <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" data-placement="top" title="Customize the colors used in PowerPoint exports to match your brand or preferences." alt=""></img>
                                    </h4>
                                    <div class="row">
                                        <div class="col-xl-3 col-lg-6 col-12">
                                            <label class="form-label">Revenue
                                                <span class="text-muted small ms-2">(<a href="#" @click.prevent="resetChartColor('revenue')" class="text-decoration-none">reset</a>)</span>
                                            </label>
                                            <div class="input-group mb-3">
                                                <input 
                                                    type="color"
                                                    :disabled="!canEdit('settings')"
                                                    class="form-control form-control-color"
                                                    v-model="chartColorHexInputs.revenue"
                                                    :style="{ 'background-color': chartColorHexInputs.revenue }"
                                                    @change="updateChartColorFromHex('revenue', $event.target.value)"
                                                 aria-label="Color picker">
                                                <input 
                                                    type="text"
                                                    class="form-control"
                                                    v-model="chartColorHexInputs.revenue"
                                                    :disabled="!canEdit('settings')"
                                                    placeholder="#98CB2E"
                                                    @change="updateChartColorFromHex('revenue', $event.target.value)"
                                                 aria-label="Text input">
                                            </div>
                                        </div>
                                        <div class="col-xl-3 col-lg-6 col-12">
                                            <label class="form-label">Donors
                                                <span class="text-muted small ms-2">(<a href="#" @click.prevent="resetChartColor('donors')" class="text-decoration-none">reset</a>)</span>
                                            </label>
                                            <div class="input-group mb-3">
                                                <input 
                                                    type="color"
                                                    :disabled="!canEdit('settings')"
                                                    class="form-control form-control-color"
                                                    v-model="chartColorHexInputs.donors"
                                                    :style="{ 'background-color': chartColorHexInputs.donors }"
                                                    @change="updateChartColorFromHex('donors', $event.target.value)"
                                                 aria-label="Color picker">
                                                <input 
                                                    type="text"
                                                    class="form-control"
                                                    v-model="chartColorHexInputs.donors"
                                                    :disabled="!canEdit('settings')"
                                                    placeholder="#5042FF"
                                                    @change="updateChartColorFromHex('donors', $event.target.value)"
                                                 aria-label="Text input">
                                            </div>
                                        </div>
                                        <div class="col-xl-3 col-lg-6 col-12">
                                            <label class="form-label">Subscribers
                                                <span class="text-muted small ms-2">(<a href="#" @click.prevent="resetChartColor('subscribers')" class="text-decoration-none">reset</a>)</span>
                                            </label>
                                            <div class="input-group mb-3">
                                                <input 
                                                    type="color"
                                                    :disabled="!canEdit('settings')"
                                                    class="form-control form-control-color"
                                                    v-model="chartColorHexInputs.subscribers"
                                                    :style="{ 'background-color': chartColorHexInputs.subscribers }"
                                                    @change="updateChartColorFromHex('subscribers', $event.target.value)"
                                                 aria-label="Color picker">
                                                <input 
                                                    type="text"
                                                    class="form-control"
                                                    v-model="chartColorHexInputs.subscribers"
                                                    :disabled="!canEdit('settings')"
                                                    placeholder="#FAAE00"
                                                    @change="updateChartColorFromHex('subscribers', $event.target.value)"
                                                 aria-label="Text input">
                                            </div>
                                        </div>
                                        <div class="col-xl-3 col-lg-6 col-12">
                                            <label class="form-label">Percentages
                                                <span class="text-muted small ms-2">(<a href="#" @click.prevent="resetChartColor('percentages')" class="text-decoration-none">reset</a>)</span>
                                            </label>
                                            <div class="input-group mb-3">
                                                <input 
                                                    type="color"
                                                    :disabled="!canEdit('settings')"
                                                    class="form-control form-control-color"
                                                    v-model="chartColorHexInputs.percentages"
                                                    :style="{ 'background-color': chartColorHexInputs.percentages }"
                                                    @change="updateChartColorFromHex('percentages', $event.target.value)"
                                                 aria-label="Color picker">
                                                <input 
                                                    type="text"
                                                    class="form-control"
                                                    v-model="chartColorHexInputs.percentages"
                                                    :disabled="!canEdit('settings')"
                                                    placeholder="#FF1951"
                                                    @change="updateChartColorFromHex('percentages', $event.target.value)"
                                                 aria-label="Text input">
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Save Button -->
                        <div class="row">
                            <div class="col">


                                <div class="alert alert-danger mb-3" v-if="form.error">
                                    There is an error in the fields above. Please correct and try again.
                                </div>
                                <button 
                                    type="button" 
                                    class="btn btn-primary px-4" 
                                    @click.prevent="update()" 
                                    :disabled="form.busy || !canEdit('settings')"
                                >
                                    <img v-if="!form.busy" class="icon me-1" src="/img/icons/avid-icons/save.svg" alt="">
                                    <div v-else class="spinner-border spinner-border-sm me-1" role="status">
                                        <span class="visually-hidden">Saving...</span>
                                    </div> Save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Fiscal Year Section -->
        <fiscal-year-settings 
            :client="local_client">
        </fiscal-year-settings>

        <!-- Image Edit Modal -->
        <image-edit-modal
            :id="'image-edit-modal'"
            :current-image="local_client.image"
            :client-url="local_client.url"
            @image-updated="handleImageUpdate"
            ref="imageEditModal"
        />
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import permissionsMixin from '../../mixins/permissions';
import { useNotifications } from '../../composables/useNotifications';
import ImageDropzone from '../components/ImageDropzone.vue';
import ImageEditModal from '../components/ImageEditModal.vue';
import FiscalYearSettings from './organization_profile/FiscalYearSettings.vue';
import Swal from 'sweetalert2';

export default defineComponent({

    mixins: [permissionsMixin],
    name: 'ProfileSettings',
    components: {
        ImageDropzone,
        ImageEditModal,
        FiscalYearSettings,
        Swal
    },
    setup() {
        const { showNotification } = useNotifications();
        return {
            showNotification
        };
    },
    props: {
        user: {
            type: Object,
            required: true
        },
        client: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            local_client: {},
            vertical_types: ['Arts, Culture, and Humanities', "Animal-Related", "Education", "Environment", "General Nonprofit", "Health & Human Services", 'Public Policy/Advocacy', 'Religion'],
            form: {
                successful: false,
                busy: false,
                error: false
            },
            errors: {
                name: "",
                vertical: "",
                slack_url: "",
                domain: "",
                image: "",
                focus: ""
            },
            chartColorInputs: {
                revenue: '',
                donors: '',
                subscribers: '',
                percentages: ''
            },
            chartColorHexInputs: {
                revenue: '#98CB2E',
                donors: '#5042FF',
                subscribers: '#FAAE00',
                percentages: '#FF1951'
            },
        };
    },
        beforeMount() {
        this.local_client = this.client;

        if (this.local_client.light_color == null)
            this.local_client.light_color = '#EFEFEF';
        if (this.local_client.dark_color == null)
            this.local_client.dark_color = '#000000';

        // Initialize chart_colors if not present
        if (!this.local_client.chart_colors) {
            this.local_client.chart_colors = {};
        }

        // Default hex colors
        const defaultHexColors = {
            revenue: '#98CB2E',
            donors: '#5042FF',
            subscribers: '#FAAE00',
            percentages: '#FF1951'
        };

        // Initialize hex inputs with existing values or defaults
        // If colors are stored in hex, use them; if in RGB, convert to hex
        this.chartColorHexInputs.revenue = this.getHexFromColor(this.local_client.chart_colors?.revenue) || defaultHexColors.revenue;
        this.chartColorHexInputs.donors = this.getHexFromColor(this.local_client.chart_colors?.donors) || defaultHexColors.donors;
        this.chartColorHexInputs.subscribers = this.getHexFromColor(this.local_client.chart_colors?.subscribers) || defaultHexColors.subscribers;
        this.chartColorHexInputs.percentages = this.getHexFromColor(this.local_client.chart_colors?.percentages) || defaultHexColors.percentages;

        // Also initialize RGB inputs for backend compatibility
        this.chartColorInputs.revenue = this.convertToRgb(this.chartColorHexInputs.revenue);
        this.chartColorInputs.donors = this.convertToRgb(this.chartColorHexInputs.donors);
        this.chartColorInputs.subscribers = this.convertToRgb(this.chartColorHexInputs.subscribers);
        this.chartColorInputs.percentages = this.convertToRgb(this.chartColorHexInputs.percentages);
    },
    mounted() {
        this.resizeTextarea();
    },
    watch: {
        // Watch for when the component becomes visible (when switching to Profile tab)
        '$parent.setting_tab_name_active': {
            handler(newTab) {
                if (newTab === 'Profile') {
                    // Use nextTick to ensure DOM is updated before resizing
                    this.$nextTick(() => {
                        this.resizeTextarea();
                    });
                }
            },
            immediate: false
        }
    },
    methods: {
        resizeTextarea() {
            const elements = document.querySelectorAll('textarea');

            for (let index = 0; index < elements.length; index++) {
                if (elements[index] && elements[index].style) {
                    elements[index].style.height = '';
                    elements[index].style.height = Math.max(elements[index].offsetHeight, elements[index].scrollHeight) + "px";
                }
            }
        },
        s3UploadSuccess(s3ObjectLocation) {
            //Stupid Amazon URL encoding my folder
            s3ObjectLocation = s3ObjectLocation.replace("%2F", "/");
            s3ObjectLocation = s3ObjectLocation.startsWith("http") ? s3ObjectLocation : 'https:'+ s3ObjectLocation;

            this.local_client.photo_url = s3ObjectLocation;
            this.local_client.image = s3ObjectLocation;
            //Only call this if they've saved the client already
            if (this.local_client.id !== -1)
                this.update();

            //Get the primary colors
            window.axios.post('/api/client/get_colors', this.local_client)
                .then(response => {
                    if (response.data.light_color != null) {
                        this.local_client.light_color = response.data.light_color;
                        this.local_client.dark_color = response.data.dark_color;
                    }
                });
        },
        openImageModal() {
            // Open the image edit modal
            const modalElement = document.getElementById('image-edit-modal');
            if (modalElement) {
                const modal = new window.bootstrap.Modal(modalElement);
                modal.show();
            }
        },
        handleImageUpdate(value) {
            if (value === '') {
                // Image was deleted
                this.local_client.photo_url = null;
                this.local_client.image = null;
                if (this.local_client.id !== -1) {
                    this.update();
                }
            } else {
                // New image was uploaded
                this.s3UploadSuccess(value);
            }
        },
        validateName() {
            window.axios.post('/api/client/check_name', this.local_client)
                .then(response => {
                    if (response.data.exists) {
                        this.errors.name = 'The client name entered already exists.';
                    }
                    else
                        this.errors.name = '';
                });
        },
        validateURL() {
            //The URL should start with https://hooks.slack.com/services/ and be a valid URL
            if (this.local_client.slack_url != null && this.local_client.slack_url !== "") {
                if (this.local_client.slack_url.indexOf("https://hooks.slack.com/services/") === -1) {
                    this.errors.slack_url = "A valid Slack Webhook URL should start with https://hooks.slack.com/services/";
                    return;
                }
            }
            this.errors.slack_url = '';
        },
        validateDomain() {
            if (this.local_client.domain && this.local_client.domain.trim() !== '') {
                // Remove any protocol and trailing slashes
                const domain = this.local_client.domain.replace(/(^\w+:|^)\/\//, '').replace(/\/$/, '');
                
                // Ensure it's a valid domain name with optional www prefix
                const regex = /^(?:www\.)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,}$/i;
                
                if (!regex.test(domain)) {
                    this.errors.domain = "Please enter a valid website domain (e.g., example.com)";
                } else {
                    this.errors.domain = '';
                    // Update the domain value to the cleaned version
                    this.local_client.domain = domain;
                }
            } else {
                this.errors.domain = '';
            }
        },
        hasError() {
            return this.errors.name !== "" || this.errors.slack_url !== "" ||
                this.errors.vertical !== "" || this.errors.domain !== "" ||
                this.errors.focus !== "";
        },
        isValid() {
            this.form.error = false;

            if (this.local_client.name === "") {
                this.errors.name = "The organization name is required.";
                this.form.error = true;
            }
            else this.errors.name = "";

            if (!this.local_client.domain || this.local_client.domain.trim() == '') {
                this.errors.domain = "The website domain is required.";
                this.form.error = true;
            } else {
                this.validateDomain();
                if (this.errors.domain !== '') {
                    this.form.error = true;
                }
            }

            if (this.local_client.vertical === "") {
                this.errors.vertical = "The vertical is required.";
                this.form.error = true;
            }
            else this.errors.vertical = "";

            if (this.local_client.focus === "") {
                this.errors.focus = "The focus is required.";
                this.form.error = true;
            }
            else this.errors.focus = "";

            return !this.form.error;
        },
        getClientDataForSave() {
            // Create a copy of the client data
            const clientData = { ...this.local_client };
            
            // Ensure chart_colors is included with current values
            if (!clientData.chart_colors) {
                clientData.chart_colors = {};
            }
            
            // Only include non-empty chart colors (convert from hex to RGB for backend)
            const chartColors = {};
            const defaultHex = {
                revenue: '#98CB2E',
                donors: '#5042FF',
                subscribers: '#FAAE00',
                percentages: '#FF1951'
            };
            
            if (this.chartColorHexInputs.revenue && this.chartColorHexInputs.revenue.toUpperCase() !== defaultHex.revenue.toUpperCase()) {
                chartColors.revenue = this.convertToRgb(this.chartColorHexInputs.revenue);
            }
            if (this.chartColorHexInputs.donors && this.chartColorHexInputs.donors.toUpperCase() !== defaultHex.donors.toUpperCase()) {
                chartColors.donors = this.convertToRgb(this.chartColorHexInputs.donors);
            }
            if (this.chartColorHexInputs.subscribers && this.chartColorHexInputs.subscribers.toUpperCase() !== defaultHex.subscribers.toUpperCase()) {
                chartColors.subscribers = this.convertToRgb(this.chartColorHexInputs.subscribers);
            }
            if (this.chartColorHexInputs.percentages && this.chartColorHexInputs.percentages.toUpperCase() !== defaultHex.percentages.toUpperCase()) {
                chartColors.percentages = this.convertToRgb(this.chartColorHexInputs.percentages);
            }
            
            // Only set chart_colors if at least one custom color is set
            if (Object.keys(chartColors).length > 0) {
                clientData.chart_colors = chartColors;
            } else {
                clientData.chart_colors = null;
            }
            
            return clientData;
        },
        updateChartColor(colorType, value) {
            // Update the input value
            this.chartColorInputs[colorType] = this.convertToRgb(value);
            
            // Update local_client.chart_colors
            if (!this.local_client.chart_colors) {
                this.local_client.chart_colors = {};
            }
            
            const rgbValue = this.convertToRgb(value);
            // Only store if different from default
            const defaults = {
                revenue: 'rgb(152 203 46)',
                donors: 'rgb(80 66 255)',
                subscribers: 'rgb(250 174 0)',
                percentages: 'rgb(255 25 81)'
            };
            
            if (rgbValue !== defaults[colorType]) {
                this.local_client.chart_colors[colorType] = rgbValue;
            } else {
                delete this.local_client.chart_colors[colorType];
            }
        },
        updateChartColorFromHex(colorType, hexValue) {
            // Ensure hex value has # prefix
            if (!hexValue.startsWith('#')) {
                hexValue = '#' + hexValue;
            }
            
            // Update hex input
            this.chartColorHexInputs[colorType] = hexValue;
            
            // Convert to RGB and update RGB input
            const rgbValue = this.convertToRgb(hexValue);
            this.chartColorInputs[colorType] = rgbValue;
            
            // Update local_client.chart_colors
            if (!this.local_client.chart_colors) {
                this.local_client.chart_colors = {};
            }
            
            // Only store if different from default
            const defaultHex = {
                revenue: '#98CB2E',
                donors: '#5042FF',
                subscribers: '#FAAE00',
                percentages: '#FF1951'
            };
            
            if (hexValue.toUpperCase() !== defaultHex[colorType].toUpperCase()) {
                this.local_client.chart_colors[colorType] = rgbValue;
            } else {
                delete this.local_client.chart_colors[colorType];
            }
        },
        resetChartColor(colorType) {
            const defaultHex = {
                revenue: '#98CB2E',
                donors: '#5042FF',
                subscribers: '#FAAE00',
                percentages: '#FF1951'
            };
            
            this.chartColorHexInputs[colorType] = defaultHex[colorType];
            this.updateChartColorFromHex(colorType, defaultHex[colorType]);
        },
        getHexFromColor(color) {
            if (!color) return null;
            
            // If already hex, return it
            if (color.startsWith('#')) {
                return color;
            }
            
            // If RGB, convert to hex
            if (color.startsWith('rgb(')) {
                return this.convertRgbToHex(color);
            }
            
            // Try to parse as hex without #
            if (/^[0-9A-Fa-f]{6}$/.test(color) || /^[0-9A-Fa-f]{3}$/.test(color)) {
                return '#' + color;
            }
            
            return null;
        },
        convertRgbToHex(rgb) {
            if (!rgb || !rgb.startsWith('rgb(')) {
                return null;
            }
            
            // Extract RGB values (handle both "rgb(152 203 46)" and "rgb(152, 203, 46)" formats)
            const match = rgb.match(/rgb\s*\(\s*(\d+)\s*[,\s]\s*(\d+)\s*[,\s]\s*(\d+)\s*\)/i);
            if (!match) {
                return null;
            }
            
            const r = parseInt(match[1]);
            const g = parseInt(match[2]);
            const b = parseInt(match[3]);
            
            // Convert to hex
            const toHex = (n) => {
                const hex = n.toString(16);
                return hex.length === 1 ? '0' + hex : hex;
            };
            
            return '#' + toHex(r) + toHex(g) + toHex(b);
        },
        convertToRgb(color) {
            if (!color || typeof color !== 'string') {
                return color;
            }
            
            // If already in rgb format, return as is
            if (color.trim().startsWith('rgb(')) {
                return color.trim();
            }
            
            // If it's a hex color (with or without #), convert to rgb
            let hex = color.trim();
            if (hex.startsWith('#')) {
                hex = hex.substring(1);
            }
            
            // Check if it's a valid hex color (3 or 6 characters)
            if (/^[0-9A-Fa-f]{3}$/.test(hex)) {
                // Expand 3-digit hex to 6-digit
                hex = hex.split('').map(char => char + char).join('');
            }
            
            if (/^[0-9A-Fa-f]{6}$/.test(hex)) {
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);
                return `rgb(${r} ${g} ${b})`;
            }
            
            // Return as is if format is unknown
            return color;
        },
        update() {
            if (this.isValid() === false)
                return;

            this.form.busy = true;

            window.axios.put('/api/client/' + this.local_client.url, this.getClientDataForSave())
                .then(response => {
                    this.form.busy = false;
                    this.form.successful = true;
                    setTimeout(() => { this.form.successful = false }, 5000);
                    this.showNotification({
                        text: 'Your profile was successfuly updated!'
                    });
                }).catch(error => {
                    this.form.busy = false;
                    this.form.error = true;
                    setTimeout(() => { this.form.error = false }, 10000);
                    this.showNotification({
                        text: 'There was an error updating your profile. Please try again.',
                        type: 'error'
                    });
                });
        },
    }
});
</script>
